import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    CircularProgress,
    FormControl,
    TextField,
    Paper,
    Typography,
    Skeleton
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useGetTenanciesQuery, useLazyGetTenanciesQuery } from "../../../../store/services/tenancy.service";
import { setTenancyId, setTenancyName, setTenancyLookup } from "../../../../store/reducers/tenancy";
import { useTheme } from "@mui/material/styles";

// Debounce delay in milliseconds
const SEARCH_DEBOUNCE_MS = 300;
// Initial page size - safe margin under 6MB limit
const INITIAL_PAGE_SIZE = 500;
// Search results page size
const SEARCH_PAGE_SIZE = 100;

const Search = memo(() => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';
    const { tenancyId, tenancyName } = useSelector((state) => state.tenancy);
    const user = useMemo(() => JSON.parse(localStorage.getItem('user')), []);

    // Initial load of tenancies (larger batch, no search query)
    const {
        data: initialTenancyData = [],
        isLoading: initialLoading,
        error: tenanciesError
    } = useGetTenanciesQuery({
        perPage: INITIAL_PAGE_SIZE,
        page: 0,
        includes: "",
        searchTerm: "",
        additionalQueryParams: ""
    });

    // Lazy query for server-side search
    const [
        triggerSearch,
        { data: searchResults = [], isLoading: searchLoading, isFetching: searchFetching }
    ] = useLazyGetTenanciesQuery();

    // Local state
    const [displayedTenancies, setDisplayedTenancies] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    // Ref for debounce timer
    const debounceTimerRef = useRef(null);

    // Redirect if no user
    useEffect(() => {
        if (!user) {
            window.location.replace('/login');
        }
    }, [user]);

    // Build lookup table when initial data loads
    useEffect(() => {
        if (initialTenancyData.length > 0) {
            const lookup = initialTenancyData.reduce((acc, tenancy) => {
                acc[tenancy.tenancyId] = tenancy.name;
                return acc;
            }, {});
            dispatch(setTenancyLookup(lookup));
            setDisplayedTenancies(initialTenancyData);
        }
    }, [initialTenancyData, dispatch]);

    // Update displayed tenancies when search results come back
    useEffect(() => {
        if (searchQuery && searchResults.length > 0) {
            // Merge search results into lookup for any new tenancies
            const currentLookup = initialTenancyData.reduce((acc, tenancy) => {
                acc[tenancy.tenancyId] = tenancy.name;
                return acc;
            }, {});

            searchResults.forEach(tenancy => {
                currentLookup[tenancy.tenancyId] = tenancy.name;
            });

            dispatch(setTenancyLookup(currentLookup));
            setDisplayedTenancies(searchResults);
            setIsSearching(false);
        } else if (searchQuery && !searchLoading && !searchFetching) {
            // Search completed with no results
            setDisplayedTenancies([]);
            setIsSearching(false);
        }
    }, [searchResults, searchQuery, searchLoading, searchFetching, initialTenancyData, dispatch]);

    // Reset to initial data when search is cleared
    useEffect(() => {
        if (!searchQuery && initialTenancyData.length > 0) {
            setDisplayedTenancies(initialTenancyData);
        }
    }, [searchQuery, initialTenancyData]);

    // Sync tenancy name from data
    useEffect(() => {
        if (initialTenancyData.length > 0 && tenancyId) {
            const currentTenancy = initialTenancyData.find(t => t.tenancyId === parseInt(tenancyId, 10));
            if (currentTenancy) {
                if (currentTenancy.name !== tenancyName) {
                    dispatch(setTenancyName(currentTenancy.name));
                }
            }
        }
    }, [initialTenancyData, tenancyId, tenancyName, dispatch]);

    // Check for stored tenancy data on mount
    useEffect(() => {
        const storedTenancyId = localStorage.getItem('selectedTenancyId');
        const storedTenancyName = localStorage.getItem('selectedTenancyName');
        if (storedTenancyId && storedTenancyName) {
            dispatch(setTenancyId(storedTenancyId));
            dispatch(setTenancyName(storedTenancyName));
            localStorage.removeItem('selectedTenancyId');
            localStorage.removeItem('selectedTenancyName');
        }
    }, [dispatch]);

    // Debounced search function
    const debouncedSearch = useCallback((query) => {
        // Clear any existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (!query || query.trim() === '') {
            // No search query - reset to initial data
            setSearchQuery('');
            setIsSearching(false);
            setDisplayedTenancies(initialTenancyData);
            return;
        }

        setIsSearching(true);

        // Set new debounce timer
        debounceTimerRef.current = setTimeout(() => {
            setSearchQuery(query);
            triggerSearch({
                perPage: SEARCH_PAGE_SIZE,
                page: 0,
                includes: "",
                searchTerm: query.trim(),
                additionalQueryParams: ""
            });
        }, SEARCH_DEBOUNCE_MS);
    }, [initialTenancyData, triggerSearch]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const handleChange = useCallback((event, newValue) => {
        if (newValue) {
            localStorage.setItem('selectedTenancyId', newValue.tenancyId);
            localStorage.setItem('selectedTenancyName', newValue.name);
            window.location.reload();
        }
    }, []);

    const handleInputChange = useCallback((event, newInputValue, reason) => {
        setInputValue(newInputValue);

        // Only trigger search on user input, not on selection or reset
        if (reason === 'input') {
            debouncedSearch(newInputValue);
        }
    }, [debouncedSearch]);

    const handleOpen = useCallback(() => {
        setOpen(true);
        setInputValue('');
        // Reset search when opening
        setSearchQuery('');
        setDisplayedTenancies(initialTenancyData);
    }, [initialTenancyData]);

    const handleClose = useCallback(() => {
        setOpen(false);
        setInputValue(tenancyName || '');
        // Clear any pending search
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        setSearchQuery('');
        setIsSearching(false);
    }, [tenancyName]);

    // Client-side filtering for immediate feedback while waiting for server
    const filterOptions = useCallback((options, params) => {
        // If we're actively searching server-side, don't filter client-side
        // (let the server results come through)
        if (searchQuery) {
            return options;
        }

        // Client-side filter for initial data (before server search kicks in)
        return options.filter((option) => {
            if (params.inputValue) {
                const inputVal = params.inputValue.toLowerCase();
                const tenancyIdString = option.tenancyId.toString();
                return option.name.toLowerCase().includes(inputVal) ||
                    tenancyIdString.includes(inputVal);
            }
            return true;
        });
    }, [searchQuery]);

    const optionLabel = useCallback((option) => {
        if (typeof option === 'string') {
            return option;
        }
        if (option.inputValue) {
            return option.inputValue;
        }
        return `${option.tenancyId} - ${option.name}`;
    }, []);

    const isOptionEqual = useCallback((option, value) => option.tenancyId === value.tenancyId, []);

    const paperComponent = useMemo(() => {
        return ({ children }) => (
            <Paper sx={{ bgcolor: 'background.default' }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    p: 1,
                    bgcolor: lightMode ? 'grey.200' : 'grey.800'
                }}>
                    <Typography variant="caption">Tenancy</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {(isSearching || searchFetching) && (
                            <CircularProgress size={12} />
                        )}
                        <Typography variant="caption">
                            {searchQuery
                                ? `Found: ${displayedTenancies.length}`
                                : `Showing: ${displayedTenancies.length}`
                            }
                        </Typography>
                        <Typography variant="caption">User Licenses</Typography>
                    </Box>
                </Box>
                {children}
            </Paper>
        );
    }, [lightMode, isSearching, searchFetching, searchQuery, displayedTenancies.length]);

    const renderOption = useCallback((props, option) => (
        <li {...props} style={{ display: 'flex', justifyContent: 'space-between', backgroundColor: 'background.default' }}>
            <span>{option.tenancyId} - {option.name}</span>
            <span>{option.userLicenses}</span>
        </li>
    ), []);

    const isLoading = initialLoading || isSearching || searchFetching;

    const renderInput = useCallback((params) => (
        <TextField
            {...params}
            label={initialLoading ? 'Loading...' : 'Select Tenancy'}
            placeholder={open ? 'Type to search all tenancies...' : ''}
            InputProps={{
                ...params.InputProps,
                variant: 'outlined',
                endAdornment: (
                    <React.Fragment>
                        {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                    </React.Fragment>
                ),
            }}
        />
    ), [initialLoading, isLoading, open]);

    // No results message
    const noOptionsText = useMemo(() => {
        if (isSearching || searchFetching) {
            return 'Searching...';
        }
        if (searchQuery && displayedTenancies.length === 0) {
            return `No tenancies found matching "${searchQuery}"`;
        }
        return 'No tenancies found';
    }, [isSearching, searchFetching, searchQuery, displayedTenancies.length]);

    // Permissions check - only super admins can see the tenancy dropdown
    if (user?.role !== "Administrators") {
        return null;
    }

    if (tenanciesError) {
        return (
            <Box sx={{ width: '30%' }}>
                <Typography color="error">
                    Error loading tenancies. Please try again later.
                </Typography>
            </Box>
        );
    }

    // Loading state
    if (initialLoading) {
        return (
            <Box sx={{ width: '30%' }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: lightMode ? 'grey.100' : 'grey.900'
                }}>
                    <CircularProgress size={20} />
                    <Skeleton variant="rounded" width="100%" height={30} />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '30%' }}>
            <FormControl sx={{ width: '100%' }}>
                <Autocomplete
                    disablePortal
                    disableClearable
                    id="tenancy-search"
                    size="small"
                    freeSolo
                    options={displayedTenancies}
                    filterOptions={filterOptions}
                    getOptionLabel={optionLabel}
                    isOptionEqualToValue={isOptionEqual}
                    value={displayedTenancies.find(row => row.tenancyId === tenancyId) || null}
                    onChange={handleChange}
                    PaperComponent={paperComponent}
                    renderOption={renderOption}
                    renderInput={renderInput}
                    onInputChange={handleInputChange}
                    inputValue={open ? inputValue : (tenancyId + " - " + tenancyName || '')}
                    open={open}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    noOptionsText={noOptionsText}
                    loading={isLoading}
                />
            </FormControl>
        </Box>
    );
});

export default Search;